<template>
  <rg-object-detail-item
    :rgObject="rgObject"
    :errorMessage="errorMessage"
    v-bind="detailProps"
    @deselect-item="deselectItem"
    @image-loading-error="onImageLoadingError"
    @image-loaded="onImageLoaded"
    @expand-picture="onExpandPicture"
  >
    <template v-slot:rg-object-icon>
      <rg-object-icon :rg-risk-level-manager="properties"></rg-object-icon>
    </template>
  </rg-object-detail-item>
</template>

<script>
import isObject from "lodash/isObject";
import RgObjectDetailItem from "./RgObjectDetailItem.vue";
import RgObjectIcon from "../RgObjectIcon.vue";
import isBoolean from "lodash/isBoolean";
import isString from "lodash/isString";
import isNumber from "lodash/isNumber";
import { createNamespacedHelpers } from "vuex";
import C from "../../../Constants";
import { storage } from "@/firebase/firebaseConfig";
const { RG_RISK_LEVEL, RADAR_GRID_OBJECT_RISK_LEVEL } = C;

const {
  mapActions: mapActionsOfRGPlayback,
  mapGetters: mapGettersOfRGPlayback,
} = createNamespacedHelpers("radarGridPlayback");

export const ERROR_TYPES = {
  NO_IMAGE: "NO_IMAGE",
  IMAGE_FETCH_FAILED: "IMAGE_FETCH_FAILED",
  OBJECT_NO_LONGER_MONITORED: "OBJECT_NO_LONGER_MONITORED",
};

const ERROR_MESSAGES = {
  [ERROR_TYPES.NO_IMAGE]: "画像はありません", // default
  [ERROR_TYPES.IMAGE_FETCH_FAILED]: "画像が取得できませんでした",
  [ERROR_TYPES.OBJECT_NO_LONGER_MONITORED]:
    "このオブジェクトは監視対象から外れました",
};

export default {
  name: "RgObjectDetailItemContainer",

  components: {
    RgObjectDetailItem,
    RgObjectIcon,
  },

  props: {
    objectId: {
      type: String,
      required: true,
    },
    rgObject: {
      type: Object, // RgObject
      required: true,
      // type RgObject = {
      //   type: string;
      //   id: string;
      //   properties: {
      //     id: string;
      //     trackNumber: string;
      //     sog: number;
      //     cog: number;
      //     k: string;
      //     icon: string;
      //     frameIcon: null | string;
      //     hasManualAssessment: boolean;
      //     autoRiskLevel: number;
      //     riskLevel: number;
      //   };
      //   geometry: {
      //     type: string;
      //     coordinates: [number, number]; // [longitude, latitude]
      //   };
      // };
    },
  },

  data() {
    return {
      loading: true, // 画像読み込み中かどうか
      imageFetchFailed: false, // imgUrlから画像取得に失敗したかどうか
      src: "",
      prevObjectState: {
        coordinates: [],
        properties: {},
      }, // 監視対象から外れた時に前の値を表示する
    };
  },
  computed: {
    ...mapGettersOfRGPlayback(["radarGridCameraMaster", "cameraHashMap"]),
    isUnmonitored() {
      return !isNumber(this.currentProperties.t); // timestampは必須なので、監視対象かどうかの判定に使用
    },
    errorMessage() {
      const { errorType } = this;
      const msg = ERROR_MESSAGES[errorType] || "";
      return msg; // string
    },
    hasRgObject() {
      return isObject(this.rgObject);
    },
    currentCoordinates() {
      const { hasRgObject, rgObject } = this;
      if (!hasRgObject) return [];
      if (!isObject(rgObject.geometry)) return [];
      if (!Array.isArray(rgObject.geometry.coordinates)) return [];
      return rgObject.geometry.coordinates; // [longitude: number, latitude: number]
    },
    // 現在または前の時間軸の座標を取得
    // 監視対象外の時は前の値を表示する
    coordinates() {
      const { currentCoordinates, prevObjectState } = this;
      const { coordinates: prevCoordinates } = prevObjectState;
      if (currentCoordinates.length === 2) return currentCoordinates;
      return prevCoordinates; // [longitude: number, latitude: number]
    },
    currentProperties() {
      const { hasRgObject, rgObject } = this;
      if (!hasRgObject) return {};
      if (!isObject(rgObject.properties)) return {};
      return rgObject.properties; // { id, trackNumber, sog, cog, k, icon, frameIcon, hasManualAssessment, autoRiskLevel, riskLevel }
    },
    // 現在または前の時間軸のpropertiesを取得
    // 監視対象外の時は前の値を表示する
    properties() {
      const { currentProperties, prevObjectState } = this;
      const { properties: prevProperties } = prevObjectState;
      if (Object.keys(currentProperties).length > 0) return currentProperties;
      return prevProperties; // { id, trackNumber, sog, cog, k, icon, frameIcon, hasManualAssessment, autoRiskLevel, riskLevel }
    },
    hasManualAssessment() {
      const { hasManualAssessment } = this.properties;
      if (!isBoolean(hasManualAssessment)) return false;
      return hasManualAssessment;
    },
    riskLevelObject() {
      const { riskLevel } = this.properties;
      const riskLevelObject =
        RADAR_GRID_OBJECT_RISK_LEVEL[riskLevel] ||
        RADAR_GRID_OBJECT_RISK_LEVEL[RG_RISK_LEVEL.CAUTION];
      return riskLevelObject;
    },
    riskLevelText() {
      const { riskLevelObject } = this;
      return riskLevelObject.labelJa;
    },
    assessmentType() {
      const { hasManualAssessment } = this;
      if (hasManualAssessment) return "（手動）";
      return "（自動）";
    },
    cameraId() {
      const { cameraId } = this.properties;
      const isCameraIdValid = isString(cameraId) && cameraId.length > 0;
      if (!isCameraIdValid) return "";
      return cameraId; // string
    },
    radarCameraMaster() {
      const { radarGridCameraMaster = {}, cameraId } = this;
      const radarCameraMaster = radarGridCameraMaster[cameraId] || null;
      return radarCameraMaster;
    },
    aiseaCameraHash() {
      if (!isObject(this.radarCameraMaster)) return "";
      return this.radarCameraMaster.a || "";
    },
    aiseaCameraName() {
      if (!isString(this.aiseaCameraHash)) return "";
      if (!this.cameraHashMap) return "";
      if (!Object.hasOwn(this.cameraHashMap, this.aiseaCameraHash)) return "";
      return this.cameraHashMap[this.aiseaCameraHash].name || "";
    },
    imgUrl() {
      const { imgUrl } = this.properties;
      return imgUrl;
    },
    detailProps() {
      const {
        properties,
        riskLevelText,
        assessmentType,
        coordinates = [],
        aiseaCameraName: cameraName,
        loading,
        src,
      } = this;
      if (!isObject(properties)) return {};
      const [longitude, latitude] = coordinates;
      return {
        ...properties,
        riskLevelText,
        assessmentType,
        longitude,
        latitude,
        cameraName,
        loading,
        src,
      };
    },
    hasImgUrl() {
      const { imgUrl } = this;
      return isString(imgUrl) && imgUrl !== ""; // 通常のurlとはkとなる
    },
    errorType() {
      const { isUnmonitored, hasImgUrl, imageFetchFailed } = this;

      if (isUnmonitored) {
        return ERROR_TYPES.OBJECT_NO_LONGER_MONITORED;
      } else if (!hasImgUrl) {
        return ERROR_TYPES.NO_IMAGE;
      } else if (imageFetchFailed) {
        return ERROR_TYPES.IMAGE_FETCH_FAILED;
      }
      return null;
    },
  },
  watch: {
    imgUrl: {
      handler(newVal) {
        this.getImgUrl(newVal);
      },
      immediate: true,
    },
    currentCoordinates() {
      if (!this.isUnmonitored) {
        this.prevObjectState.coordinates = this.currentCoordinates;
      }
    },
    currentProperties() {
      if (!this.isUnmonitored) {
        this.prevObjectState.properties = this.currentProperties;
      }
    },
  },
  methods: {
    ...mapActionsOfRGPlayback([
      "removeVisibleTracksRgObject",
      "filterAndAddExpandImgRgObjectById",
    ]),
    deselectItem() {
      const payload = { toBeRemovedId: this.objectId };
      this.removeVisibleTracksRgObject(payload);
    },
    async getImgUrl(path) {
      try {
        this.loading = true;
        this.imageFetchFailed = false;
        this.src = await storage.child(path).getDownloadURL();
      } catch (error) {
        this.src = ""; // 画像取得に失敗した時は画像のURLをリセット
        this.onImageLoadingError();
      }
    },
    // 画像読み込み失敗時のハンドリング
    onImageLoadingError() {
      this.imageFetchFailed = true;
      this.loading = false;
    },
    // 画像読み込み完了時のハンドリング
    onImageLoaded() {
      this.imageFetchFailed = false;
      this.loading = false;
    },
    onExpandPicture() {
      const { src, aiseaCameraName } = this;
      const { id, trackNumber, riskLevel } = this.properties;
      const expandImgRgObject = {
        id,
        trackNumber,
        src,
        aiseaCameraName,
        riskLevel,
      };
      this.filterAndAddExpandImgRgObjectById({ rgObject: expandImgRgObject });
    },
  },
};
</script>
